import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleList } from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import swal from "sweetalert";
import { APIMS } from "../../../../../config/apims";
import { APISD } from "../../../../../config/apisd";

const emptyData = { header: [], informasi: {}, data_murid: [] };

const normalizeChildren = (children, matpelId, colspan = 0) => {
  if (Array.isArray(children) && children.length) {
    return children.map((item, index) => {
      const name =
        item?.name_semester ?? item?.semester ?? item?.title ?? index + 1;
      return {
        semester_id:
          item?.semester_id ||
          item?.ysb_semester_id ||
          item?.id ||
          `${matpelId}-semester-${index}`,
        name_semester: name,
        label: `Semester ${name}`,
      };
    });
  }

  return Array.from({ length: Number(colspan || 0) }, (_, index) => ({
    semester_id: `${matpelId}-semester-${index + 1}`,
    name_semester: index + 1,
    label: `Semester ${index + 1}`,
  }));
};

export default function TranskipNilai() {
  const initialTranscriptLoaded = useRef(false);
  const token = localStorage.getItem("token") || "";
  const accessLevel = localStorage.getItem("level") || "";
  const branchGrouping = localStorage.getItem("ysb_branch_id") || "";

  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(false);
  const [loadingLevel, setLoadingLevel] = useState(false);
  const [loadingClass, setLoadingClass] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [years, setYears] = useState([]);
  const [branches, setBranches] = useState([]);
  const [schools, setSchools] = useState([]);
  const [levels, setLevels] = useState([]);
  const [classes, setClasses] = useState([]);
  const [hasRequested, setHasRequested] = useState(false);
  const [transcript, setTranscript] = useState(emptyData);
  const [emptyMessage, setEmptyMessage] = useState();
  const [filter, setFilter] = useState({
    ysb_academic_year_id: "",
    ysb_branch_id: "",
    ysb_school_id: "",
    ysb_grade_level_id: "",
    ysb_kelas_id: "",
  });

  useEffect(() => {
    document.title = "Transkip Nilai";
  }, []);

  const requestConfig = useMemo(
    () => ({
      headers: { Authorization: token, "Content-Type": "application/json" },
    }),
    [token],
  );

  const showError = useCallback((text) => {
    swal({ title: "Gagal", text, icon: "error", timer: 3000, buttons: false });
  }, []);

  const clearResult = useCallback(() => {
    setTranscript(emptyData);
    setHasRequested(false);
    // setEmptyMessage(
    //   "...",
    // );
  }, []);

  const loadInitialFilter = useCallback(async () => {
    setLoadingInit(true);
    try {
      const [branchResponse, semesterResponse] = await Promise.all([
        APIMS.get("/api/access/branches", {
          ...requestConfig,
          params: {
            page: 1,
            limit: 1000,
            ascending: 1,
            search: "",
            branch: branchGrouping,
            level: accessLevel,
          },
        }),
        APIMS.get("/api/access/semesters", {
          ...requestConfig,
          params: { page: 1, limit: 1000, ascending: 0, search: "" },
        }),
      ]);

      const branchRows = branchResponse?.data?.data || [];
      const semesterRows = semesterResponse?.data?.data || [];
      const yearMap = new Map();

      semesterRows.forEach((semester) => {
        const id = semester?.ysb_academic_year_id;
        if (!id) return;
        const old = yearMap.get(id);
        if (!old || Number(semester?.status) === 1) {
          yearMap.set(id, {
            id,
            name_year: semester?.name_year || "-",
            status: Number(semester?.status || 0),
          });
        }
      });

      const yearRows = Array.from(yearMap.values()).sort((a, b) =>
        String(b.name_year).localeCompare(String(a.name_year), "id", {
          numeric: true,
        }),
      );
      const activeYear =
        yearRows.find((item) => item.status === 1) || yearRows[0];

      setYears(yearRows);
      setBranches(branchRows);
      setFilter((old) => ({
        ...old,
        ysb_academic_year_id: activeYear?.id || "",
        ysb_branch_id: branchRows[0]?.branch_code || "",
      }));
    } catch (error) {
      showError(
        error?.response?.data?.message || "Gagal mengambil filter awal",
      );
    } finally {
      setLoadingInit(false);
    }
  }, [accessLevel, branchGrouping, requestConfig, showError]);

  useEffect(() => {
    loadInitialFilter();
  }, [loadInitialFilter]);

  const loadSchools = useCallback(
    async (branchId) => {
      if (!branchId) return;
      setLoadingSchool(true);
      try {
        const response = await APIMS.get("/api/access/schools", {
          ...requestConfig,
          params: {
            page: 1,
            limit: 1000,
            ascending: 1,
            branch: branchId,
            level: accessLevel,
          },
        });
        let rows = response?.data?.data || [];
        if (accessLevel === "developer" || accessLevel === "superadmin") {
          rows = rows.filter((item) => item?.ysb_branch_id === branchId);
        }
        if (branchId === "BKS") {
          rows = rows.filter((item) => item?.school_code !== "BASEMENT_BEKASI");
        }
        const order = ["SD", "SMP", "SMA"];
        rows.sort((a, b) => {
          const aName = String(a?.school_name || "").toUpperCase();
          const bName = String(b?.school_name || "").toUpperCase();
          return (
            order.findIndex((item) => aName.includes(item)) -
            order.findIndex((item) => bName.includes(item))
          );
        });
        setSchools(rows);
        setFilter((old) => ({
          ...old,
          ysb_school_id: rows[0]?.school_code || "",
          ysb_grade_level_id: "",
          ysb_kelas_id: "",
        }));
      } catch (error) {
        setSchools([]);
        showError(error?.response?.data?.message || "Gagal mengambil sekolah");
      } finally {
        setLoadingSchool(false);
      }
    },
    [accessLevel, requestConfig, showError],
  );

  useEffect(() => {
    setSchools([]);
    setLevels([]);
    setClasses([]);
    clearResult();
    if (filter.ysb_branch_id) loadSchools(filter.ysb_branch_id);
  }, [filter.ysb_branch_id, loadSchools, clearResult]);

  const loadLevels = useCallback(
    async (schoolId, branchId) => {
      if (!schoolId || !branchId) return;
      setLoadingLevel(true);
      try {
        const response = await APIMS.get("/api/grade-levels", {
          ...requestConfig,
          params: {
            page: 1,
            limit: 1000,
            ascending: 1,
            branch: branchId,
            level: accessLevel,
          },
        });
        const rows = (response?.data?.data || [])
          .filter(
            (item) =>
              item?.ysb_school_id === schoolId &&
              item?.ysb_branch_id === branchId,
          )
          .sort((a, b) =>
            String(a?.name_level || "").localeCompare(
              String(b?.name_level || ""),
              "id",
              { numeric: true },
            ),
          );
        setLevels(rows);
        setFilter((old) => ({
          ...old,
          ysb_grade_level_id: rows[0]?.id || "",
          ysb_kelas_id: "",
        }));
      } catch (error) {
        setLevels([]);
        showError(
          error?.response?.data?.message || "Gagal mengambil tingkatan",
        );
      } finally {
        setLoadingLevel(false);
      }
    },
    [accessLevel, requestConfig, showError],
  );

  useEffect(() => {
    setLevels([]);
    setClasses([]);
    clearResult();
    if (filter.ysb_school_id && filter.ysb_branch_id) {
      loadLevels(filter.ysb_school_id, filter.ysb_branch_id);
    }
  }, [filter.ysb_school_id, filter.ysb_branch_id, loadLevels, clearResult]);

  const loadClasses = useCallback(
    async (levelId, schoolId, branchId) => {
      if (!levelId || !schoolId || !branchId) return;
      setLoadingClass(true);
      try {
        const response = await APIMS.get("/api/kelas", {
          ...requestConfig,
          params: {
            page: 1,
            limit: 1000,
            ascending: 1,
            branch: branchId,
            level: accessLevel,
          },
        });
        const rows = (response?.data?.data || [])
          .filter(
            (item) =>
              item?.ysb_grade_level_id === levelId &&
              item?.ysb_school_id === schoolId &&
              item?.ysb_branch_id === branchId,
          )
          .sort((a, b) =>
            String(a?.name_kelas || "").localeCompare(
              String(b?.name_kelas || ""),
              "id",
              { numeric: true },
            ),
          );
        setClasses(rows);
        setFilter((old) => ({ ...old, ysb_kelas_id: rows[0]?.id || "" }));
      } catch (error) {
        setClasses([]);
        showError(error?.response?.data?.message || "Gagal mengambil kelas");
      } finally {
        setLoadingClass(false);
      }
    },
    [accessLevel, requestConfig, showError],
  );

  useEffect(() => {
    setClasses([]);
    clearResult();
    if (
      filter.ysb_grade_level_id &&
      filter.ysb_school_id &&
      filter.ysb_branch_id
    ) {
      loadClasses(
        filter.ysb_grade_level_id,
        filter.ysb_school_id,
        filter.ysb_branch_id,
      );
    }
  }, [
    filter.ysb_grade_level_id,
    filter.ysb_school_id,
    filter.ysb_branch_id,
    loadClasses,
    clearResult,
  ]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilter((old) => {
      const next = { ...old, [name]: value };
      if (name === "ysb_branch_id") {
        next.ysb_school_id = "";
        next.ysb_grade_level_id = "";
        next.ysb_kelas_id = "";
      } else if (name === "ysb_school_id") {
        next.ysb_grade_level_id = "";
        next.ysb_kelas_id = "";
      } else if (name === "ysb_grade_level_id") {
        next.ysb_kelas_id = "";
      }
      return next;
    });
    clearResult();
  };

  const filterComplete =
    filter.ysb_academic_year_id &&
    filter.ysb_branch_id &&
    filter.ysb_school_id &&
    filter.ysb_grade_level_id &&
    filter.ysb_kelas_id &&
    !loadingSchool &&
    !loadingLevel &&
    !loadingClass;

  const loadTranscript = useCallback(async () => {
    if (!filterComplete) return;
    setLoadingTranscript(true);
    setHasRequested(true);
    try {
      const payload = {
        ysb_academic_year_id: filter.ysb_academic_year_id,
        ysb_kelas_id: filter.ysb_kelas_id,
        ysb_branch_id: filter.ysb_branch_id,
        ysb_school_id: filter.ysb_school_id,
      };
      const response = await APISD.request({
        method: "GET",
        url: "/api/book-exam-score-recaps/show",
        headers: requestConfig.headers,
        params: payload,
      });
      const data = response?.data?.data || {};
      setTranscript({
        header: Array.isArray(data.header) ? data.header : [],
        informasi: data.informasi || {},
        data_murid: Array.isArray(data.data_murid) ? data.data_murid : [],
      });
      setEmptyMessage("Data nilai siswa belum tersedia");
    } catch (error) {
      setTranscript(emptyData);
      setEmptyMessage("Data nilai siswa belum tersedia");
      showError(
        error?.response?.data?.message ||
          error?.response?.data?.error?.message ||
          "Gagal mengambil data transkip nilai",
      );
    } finally {
      setLoadingTranscript(false);
    }
  }, [
    filter.ysb_academic_year_id,
    filter.ysb_branch_id,
    filter.ysb_kelas_id,
    filter.ysb_school_id,
    filterComplete,
    requestConfig.headers,
    showError,
  ]);

  useEffect(() => {
    if (filterComplete && !initialTranscriptLoaded.current) {
      initialTranscriptLoaded.current = true;
      loadTranscript();
    }
  }, [filterComplete, loadTranscript]);

  const headers = useMemo(() => {
    const apiHeaders = (transcript.header || [])
      .filter((item) => item?.matpel_id || item?.ysb_matpel_id)
      .map((item, index) => {
        const id =
          item?.matpel_id ||
          item?.ysb_matpel_id ||
          item?.id ||
          `header-${index}`;
        return {
          matpel_id: id,
          title:
            item?.title ||
            item?.name_matpel ||
            item?.nama_matpel ||
            `Matpel ${index + 1}`,
          children: normalizeChildren(item?.children, id, item?.colspan),
        };
      })
      .filter((item) => item.children.length);
    if (apiHeaders.length) return apiHeaders;

    const map = new Map();
    (transcript.data_murid || []).forEach((student) => {
      (student?.data_nilai || []).forEach((score, scoreIndex) => {
        const id =
          score?.matpel_id ||
          score?.ysb_matpel_id ||
          score?.id ||
          `matpel-${scoreIndex}`;
        if (!map.has(id)) {
          map.set(id, {
            matpel_id: id,
            title:
              score?.name_matpel ||
              score?.nama_matpel ||
              `Matpel ${scoreIndex + 1}`,
            children: [],
          });
        }
        const target = map.get(id);
        (score?.semesters || []).forEach((semester, semesterIndex) => {
          const semesterId =
            semester?.semester_id || semester?.ysb_semester_id || semester?.id;
          const name =
            semester?.name_semester ?? semester?.semester ?? semesterIndex + 1;
          if (
            !target.children.some(
              (child) => String(child.name_semester) === String(name),
            )
          ) {
            target.children.push({
              semester_id: semesterId || `${id}-semester-${semesterIndex}`,
              name_semester: name,
              label: `Semester ${name}`,
            });
          }
        });
      });
    });
    return Array.from(map.values()).filter((item) => item.children.length);
  }, [transcript]);

  const collator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    [],
  );
  const students = useMemo(
    () =>
      [...(transcript.data_murid || [])].sort((a, b) =>
        collator.compare(
          a?.nama_lengkap || a?.nama || "",
          b?.nama_lengkap || b?.nama || "",
        ),
      ),
    [collator, transcript.data_murid],
  );
  const totalScoreColumns = headers.reduce(
    (total, subject) => total + subject.children.length,
    0,
  );

  const getScore = (student, subject, semester) => {
    const subjectScore = (student?.data_nilai || []).find(
      (item) =>
        String(item?.matpel_id || item?.ysb_matpel_id || item?.id) ===
        String(subject.matpel_id),
    );
    const semesterScore = (subjectScore?.semesters || []).find((item) => {
      const id = item?.semester_id || item?.ysb_semester_id || item?.id;
      return (
        (id && String(id) === String(semester.semester_id)) ||
        String(item?.name_semester) === String(semester.name_semester)
      );
    });
    const value = semesterScore?.nilai_rapor;
    return value === 0 || value === "0" || value ? value : "-";
  };

  return (
    <div className="body sims-page">
      <style>{`
        .transkip-table-wrap{width:100%;max-height:calc(100vh - 300px);overflow:auto;border:1px solid #dbe4ee;border-radius:10px;background:#fff;scrollbar-color:#cbd5e1 transparent;scrollbar-width:thin;-webkit-overflow-scrolling:touch}
        .transkip-table-wrap::-webkit-scrollbar{width:9px;height:9px}.transkip-table-wrap::-webkit-scrollbar-thumb{border-radius:999px;background:#cbd5e1}
        .transkip-table{width:max-content!important;min-width:100%;margin-bottom:0!important;border-collapse:separate!important;border-spacing:0!important;color:#334155;font-size:13px}
        .transkip-table th,.transkip-table td{height:58px;padding:12px 14px!important;border:0!important;border-right:1px solid #e2e8f0!important;border-bottom:1px solid #e2e8f0!important;text-align:center;vertical-align:middle!important;white-space:nowrap}
        .transkip-table thead th{position:sticky;top:0;z-index:6;height:58px;background:#f2f3fc!important;color:#172554!important;font-weight:700;white-space:normal}
        .transkip-table thead tr:nth-child(2) th{top:58px;z-index:7}.transkip-table tbody tr:nth-child(odd) td{background:#fff!important}.transkip-table tbody tr:nth-child(even) td{background:#f8fafc!important}.transkip-table tbody tr:hover td{background:#f1f5ff!important}
        .transkip-table .transkip-no-column{position:sticky;left:0;z-index:3;width:70px;min-width:70px;max-width:70px;padding:8px!important}.transkip-table thead .transkip-no-column{z-index:10;background:#f2f3fc!important}
        .transkip-table .transkip-student-column{position:sticky;left:70px;z-index:3;width:300px;min-width:300px;max-width:300px;text-align:left;white-space:normal;box-shadow:5px 0 10px -10px rgba(0,76,255,.55)}.transkip-table thead .transkip-student-column{z-index:10;background:#f2f3fc!important}.transkip-table tbody .transkip-student-column{color:#1f2937;font-weight:600}
        .transkip-number{display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border:1px solid #d4ddff;border-radius:50%;background:#f5f7ff;color:#3f5bea;font-weight:700}.transkip-score-column{width:110px;min-width:110px;color:#000;font-weight:700}
        .transkip-heading{display:flex;align-items:center;justify-content:space-between;margin:0 0 14px;gap:12px}.transkip-heading h5{margin:0;color:#111827;font-size:16px;font-weight:700}.transkip-heading span{color:#64748b;font-size:13px;font-weight:600}.transkip-empty{padding:45px 20px;border:1px dashed #cbd5e1;border-radius:12px;background:#fff;color:#64748b;text-align:center;font-weight:600}
        @media(max-width:780px){.transkip-table-wrap{max-height:calc(100vh - 230px)}.transkip-table .transkip-student-column{width:220px;min-width:220px;max-width:220px}.transkip-heading{align-items:flex-start;flex-direction:column}}
      `}</style>

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faRectangleList} />
            </span>
            <h4 className="page-title-text">Transkip Nilai</h4>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {loadingInit ? (
          <div className="sims-filter-card">
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} className="shimmer sims-filter-skeleton" />
            ))}
          </div>
        ) : (
          <div className="sims-filter-card">
            <Filter
              label="Tahun Ajaran"
              name="ysb_academic_year_id"
              value={filter.ysb_academic_year_id}
              onChange={handleChange}
            >
              <option value="" disabled>
                Pilih Tahun Ajaran
              </option>
              {years.map((item) => (
                <option key={item.id} value={item.id}>
                  {String(item.name_year).replace("-", "/")}
                </option>
              ))}
            </Filter>
            <Filter
              label="Cabang"
              name="ysb_branch_id"
              value={filter.ysb_branch_id}
              onChange={handleChange}
            >
              <option value="" disabled>
                Pilih Cabang
              </option>
              {branches.map((item, index) => (
                <option
                  key={item?.branch_code || index}
                  value={item?.branch_code}
                >
                  {item?.branch_name}
                </option>
              ))}
            </Filter>
            <Filter
              label="Sekolah"
              name="ysb_school_id"
              value={filter.ysb_school_id}
              onChange={handleChange}
              disabled={loadingSchool || !filter.ysb_branch_id}
            >
              <option value="" disabled>
                {loadingSchool ? "Memuat sekolah..." : "Pilih Sekolah"}
              </option>
              {schools.map((item, index) => (
                <option
                  key={item?.school_code || index}
                  value={item?.school_code}
                >
                  {item?.school_name}
                </option>
              ))}
            </Filter>
            <Filter
              label="Tingkatan"
              name="ysb_grade_level_id"
              value={filter.ysb_grade_level_id}
              onChange={handleChange}
              disabled={loadingLevel || !filter.ysb_school_id}
            >
              <option value="" disabled>
                {loadingLevel ? "Memuat tingkatan..." : "Pilih Tingkatan"}
              </option>
              {levels.map((item, index) => (
                <option key={item?.id || index} value={item?.id}>
                  {item?.name_level}
                </option>
              ))}
            </Filter>
            <Filter
              label="Kelas"
              name="ysb_kelas_id"
              value={filter.ysb_kelas_id}
              onChange={handleChange}
              disabled={loadingClass || !filter.ysb_grade_level_id}
            >
              <option value="" disabled>
                {loadingClass ? "Memuat kelas..." : "Pilih Kelas"}
              </option>
              {classes.map((item, index) => (
                <option key={item?.id || index} value={item?.id}>
                  {item?.name_kelas}
                </option>
              ))}
            </Filter>
            <div className="sims-filter-action">
              <button
                type="button"
                onClick={loadTranscript}
                disabled={!filterComplete || loadingTranscript}
                className="sims-filter-button"
              >
                Tampilkan Data
              </button>
            </div>
          </div>
        )}

        {loadingTranscript ? (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        ) : hasRequested && students.length ? (
          <div>
            <div className="transkip-heading">
              <h5>Transkip Nilai</h5>
              <span>
                {transcript?.informasi?.name_level
                  ? `Kelas ${transcript.informasi.name_level} - `
                  : ""}
                {transcript?.informasi?.name_kelas || ""}
              </span>
            </div>
            <div className="transkip-table-wrap">
              <table
                className="table table-hover transkip-table"
                style={{
                  minWidth: 70 + 300 + Math.max(totalScoreColumns, 1) * 110,
                }}
              >
                <thead>
                  <tr>
                    <th rowSpan={2} className="transkip-no-column text-center">
                      No
                    </th>
                    <th
                      rowSpan={2}
                      className="transkip-student-column text-center"
                    >
                      Siswa
                    </th>
                    {headers.map((subject) => (
                      <th
                        key={subject.matpel_id}
                        colSpan={subject.children.length}
                        style={{ minWidth: subject.children.length * 110 }}
                        className="text-center"
                      >
                        {subject.title}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {headers.flatMap((subject) =>
                      subject.children.map((semester) => (
                        <th
                          key={`${subject.matpel_id}-${semester.semester_id}`}
                          className="transkip-score-column text-center"
                        >
                          {semester.label}
                        </th>
                      )),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={student?.ysb_student_id || index}>
                      <td className="transkip-no-column text-center">
                        <span className="transkip-number ">{index + 1}</span>
                      </td>
                      <td className="transkip-student-column">
                        {student?.nama_lengkap || student?.nama || "-"}
                      </td>
                      {headers.flatMap((subject) =>
                        subject.children.map((semester) => {
                          const score = getScore(student, subject, semester);
                          return (
                            <td
                              key={`${student?.ysb_student_id || index}-${subject.matpel_id}-${semester.semester_id}`}
                              className="transkip-score-column text-center"
                              style={{
                                color: score === "-" ? "#94a3b8" : undefined,
                                fontWeight: score === "-" ? 500 : 700,
                              }}
                            >
                              {score}
                            </td>
                          );
                        }),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="transkip-empty"></div>
        )}
      </div>
    </div>
  );
}

function Filter({ label, children, ...selectProps }) {
  return (
    <div className="sims-filter-field">
      <label className="sims-filter-label">{label}</label>
      <select className="sims-filter-select" {...selectProps}>
        {children}
      </select>
    </div>
  );
}
